import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "../api/axios";

export const AuthContext = createContext(null);

function normalizeAuthPayload(payload) {
  const data = payload?.data || payload || {};

  return {
    user: data?.user || data || null,
    roles: Array.isArray(data?.roles) ? data.roles : [],
    permissions: Array.isArray(data?.permissions) ? data.permissions : [],
    menus: Array.isArray(data?.menus) ? data.menus : [],
  };
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState({
    token: localStorage.getItem("payroll_token") || null,
    user: null,
    roles: [],
    permissions: [],
    menus: [],
    loading: true,
  });

  const clearAuth = useCallback(() => {
    localStorage.removeItem("payroll_token");
    localStorage.removeItem("payroll_auth");

    setAuth({
      token: null,
      user: null,
      roles: [],
      permissions: [],
      menus: [],
      loading: false,
    });
  }, []);

  const loadProfile = useCallback(async () => {
    const token = localStorage.getItem("payroll_token");

    if (!token) {
      setAuth((prev) => ({ ...prev, loading: false }));
      return null;
    }

    try {
      const res = await axios.get("/auth/me");
      const normalized = normalizeAuthPayload(res.data);

      const nextAuth = {
        token,
        user: normalized.user,
        roles: normalized.roles,
        permissions: normalized.permissions,
        menus: normalized.menus,
        loading: false,
      };

      setAuth(nextAuth);
      localStorage.setItem("payroll_auth", JSON.stringify(nextAuth));
      return nextAuth;
    } catch (error) {
      clearAuth();
      return null;
    }
  }, [clearAuth]);

  const login = useCallback(
    async (credentials) => {
      const payload = {
        password: credentials.password,
      };

      if (credentials.identifier?.includes("@")) {
        payload.email = credentials.identifier;
      } else {
        payload.username = credentials.identifier;
      }

      const res = await axios.post("/login", payload);
      const token = res.data?.access_token;

      if (!token) {
        throw new Error("No access token returned from server.");
      }

      localStorage.setItem("payroll_token", token);

      const normalized = normalizeAuthPayload(res.data);

      const nextAuth = {
        token,
        user: normalized.user,
        roles: normalized.roles,
        permissions: normalized.permissions,
        menus: normalized.menus,
        loading: false,
      };

      setAuth(nextAuth);
      localStorage.setItem("payroll_auth", JSON.stringify(nextAuth));

      if (!nextAuth.user) {
        await loadProfile();
      }

      return nextAuth;
    },
    [loadProfile]
  );

  const logout = useCallback(async () => {
    try {
      await axios.post("/logout");
    } catch (error) {
      // ignore logout API error
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  const refreshAccess = useCallback(async () => {
    return loadProfile();
  }, [loadProfile]);

  const hasPermission = useCallback(
    (permissionCode) => {
      if (!permissionCode) return true;
      return auth.permissions.includes(permissionCode);
    },
    [auth.permissions]
  );

  const hasRole = useCallback(
    (roleCode) => {
      return auth.roles.some((role) => role?.code === roleCode);
    },
    [auth.roles]
  );

  useEffect(() => {
    const cached = localStorage.getItem("payroll_auth");
    const token = localStorage.getItem("payroll_token");

    if (!token) {
      setAuth((prev) => ({ ...prev, loading: false }));
      return;
    }

    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        setAuth({
          token,
          user: parsed?.user || null,
          roles: Array.isArray(parsed?.roles) ? parsed.roles : [],
          permissions: Array.isArray(parsed?.permissions) ? parsed.permissions : [],
          menus: Array.isArray(parsed?.menus) ? parsed.menus : [],
          loading: false,
        });
      } catch (error) {
        setAuth((prev) => ({ ...prev, loading: true }));
      }
    }

    loadProfile();
  }, [loadProfile]);

  const value = useMemo(
    () => ({
      ...auth,
      login,
      logout,
      refreshAccess,
      clearAuth,
      hasPermission,
      hasRole,
      isAuthenticated: !!auth.token,
    }),
    [auth, login, logout, refreshAccess, clearAuth, hasPermission, hasRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
